// The context menu of a contents row or a card (the Mac's binder and card menus, with StatusLabelMenus): new items
// beside it, rename, convert, Status and Label, Move to Trash. Acts on the selection when the item is part of it.
import { isNumberedChapter, type MetadataValue } from '@writing-suite/core';
import { structureEdits, type Workspace } from '../shell/workspace.ts';
import { arrangementNoun } from '../store/binderFilter.ts';
import { choiceEntries } from './FieldEditor.tsx';
import type { MenuEntry } from './Menu.tsx';
import { locate } from './structure.ts';

export function itemMenu(w: Workspace, nodeIds: string[], o: { onRename?: (nodeId: string) => void; onCreated?: (nodeId: string) => void }): MenuEntry[] {
  if (!w.canStructure || !nodeIds.length) return [{ kind: 'heading', label: w.arrangement.role === 'trash' ? 'In the Trash' : 'Read only' }];
  const edits = structureEdits(w);
  const first = locate(w.tree, nodeIds[0])?.node;
  if (!first) return [];
  const noun = arrangementNoun(w.project.type, w.arrangement.role);
  const entries: MenuEntry[] = [
    { label: noun === 'post' ? 'New Post' : noun === 'scene' ? 'New Scene' : 'New Document', onSelect: () => { const id = edits.newItem(first.id, false); if (id) o.onCreated?.(id); } },
    // A journal's post or section: a new section in that post.
    ...(nodeIds.length === 1 && edits.canAddSection(first.id)
      ? [{ label: 'New Section', onSelect: () => { const id = edits.newSection(first.id); if (id) o.onCreated?.(id); } }]
      : []),
    { label: 'New Folder', onSelect: () => { const id = edits.newItem(first.id, true); if (id) o.onCreated?.(id); } },
    { kind: 'separator' },
  ];
  if (nodeIds.length === 1 && first.fragmentId) entries.push({ label: 'Rename', shortcut: 'F2', onSelect: () => o.onRename?.(first.id) });
  const allFolders = nodeIds.every((id) => locate(w.tree, id)?.node.isFolder);
  entries.push({ label: allFolders ? 'Convert to Document' : 'Convert to Folder', onSelect: () => edits.setFolder(nodeIds, !allFolders) });
  // Lock, or Unlock when every chosen document carries the lock itself.
  const own = nodeIds.map((id) => locate(w.tree, id)?.node.fragmentId).filter((id): id is string => !!id);
  const allLocked = own.length > 0 && own.every((id) => w.project.fragment(id)?.locked);
  if (own.length) entries.push({ label: allLocked ? 'Unlock' : 'Lock', onSelect: () => edits.setLocked(nodeIds, !allLocked) });
  // Numbered (a tick) for chapters and parts: off for a prologue, which compile and the contents count past.
  const openers = w.project.openers();
  const chapterish = own.filter((id) => openers.has(id));
  if (chapterish.length) {
    const allNumbered = chapterish.every((id) => isNumberedChapter(w.project.fragment(id)?.metadata));
    entries.push({ kind: 'check', label: 'Numbered', checked: allNumbered, onSelect: () => edits.setNumbered(nodeIds, !allNumbered) });
  }
  const fields = w.project.fields();
  const entities = w.project.entities();
  for (const id of ['status', 'label']) {
    const field = fields.find((f) => f.id === id);
    if (!field || !field.options.length) continue;
    const value: MetadataValue | undefined = first.fragmentId ? w.project.fragment(first.fragmentId)?.metadata[id] : undefined;
    entries.push({ kind: 'separator' }, { kind: 'heading', label: field.name });
    entries.push(...choiceEntries(field, entities, value, (v) => edits.setField(nodeIds, id, v)));
  }
  entries.push({ kind: 'separator' }, { label: 'Move to Trash…', danger: true, shortcut: '⌫', onSelect: () => w.askTrash(nodeIds) });
  return entries;
}
